<template>
  <div>
    <!-- Hero Section -->
    <section class="bg-gradient-to-br from-qup-blue to-qup-dark text-white">
      <div class="container-max section-padding">
        <div class="text-center">
          <h1 class="text-4xl md:text-5xl font-bold mb-6">
            Our Products
          </h1>
          <p class="text-xl text-gray-200 max-w-3xl mx-auto">
            Innovative technology solutions designed to address complex business challenges and drive digital transformation across industries.
          </p>
        </div>
      </div>
    </section>

    <!-- Products Grid -->
    <section class="section-padding">
      <div class="container-max">
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <!-- Product 1 -->
          <div class="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
            <div class="h-48 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <div class="text-white text-center">
                <div class="text-6xl mb-4">🔐</div>
                <h3 class="text-2xl font-bold">QupSecure</h3>
              </div>
            </div>
            <div class="p-6">
              <h3 class="text-xl font-semibold text-qup-dark mb-3">QupSecure Platform</h3>
              <p class="text-gray-600 mb-4">
                Comprehensive cybersecurity platform that provides real-time threat detection, vulnerability assessment, and automated incident response capabilities.
              </p>
              <div class="space-y-2 mb-6">
                <div v-for="feature in qupSecureFeatures" :key="feature" class="flex items-center text-sm text-gray-600">
                  <svg class="w-4 h-4 text-qup-blue mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  {{ feature }}
                </div>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-2xl font-bold text-qup-dark">Coming Soon</span>
                <button class="btn-primary text-sm">
                  Learn More
                </button>
              </div>
            </div>
          </div>

          <!-- Product 2 -->
          <div class="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
            <div class="h-48 bg-gradient-to-br from-green-500 to-blue-600 flex items-center justify-center">
              <div class="text-white text-center">
                <div class="text-6xl mb-4">⚡</div>
                <h3 class="text-2xl font-bold">QupFlow</h3>
              </div>
            </div>
            <div class="p-6">
              <h3 class="text-xl font-semibold text-qup-dark mb-3">QupFlow Optimizer</h3>
              <p class="text-gray-600 mb-4">
                Intelligent process optimization platform that analyzes workflows, identifies bottlenecks, and provides actionable insights to improve operational efficiency.
              </p>
              <div class="space-y-2 mb-6">
                <div v-for="feature in qupFlowFeatures" :key="feature" class="flex items-center text-sm text-gray-600">
                  <svg class="w-4 h-4 text-qup-blue mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  {{ feature }}
                </div>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-2xl font-bold text-qup-dark">Coming Soon</span>
                <button class="btn-primary text-sm">
                  Learn More
                </button>
              </div>
            </div>
          </div>

          <!-- Product 3 -->
          <div class="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
            <div class="h-48 bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center">
              <div class="text-white text-center">
                <div class="text-6xl mb-4">🔗</div>
                <h3 class="text-2xl font-bold">QupConnect</h3>
              </div>
            </div>
            <div class="p-6">
              <h3 class="text-xl font-semibold text-qup-dark mb-3">QupConnect Integration Hub</h3>
              <p class="text-gray-600 mb-4">
                Enterprise-grade integration platform that seamlessly connects disparate systems, enabling data flow and process automation across your entire organization.
              </p>
              <div class="space-y-2 mb-6">
                <div v-for="feature in qupConnectFeatures" :key="feature" class="flex items-center text-sm text-gray-600">
                  <svg class="w-4 h-4 text-qup-blue mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  {{ feature }}
                </div>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-2xl font-bold text-qup-dark">Coming Soon</span>
                <button class="btn-primary text-sm">
                  Learn More
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Product Features -->
    <section class="section-padding bg-gray-50">
      <div class="container-max">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold text-qup-dark mb-4">
            Why Choose Our Products?
          </h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Our products are built with enterprise-grade technology, designed for scalability, security, and ease of use to meet the demands of modern businesses.
          </p>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div class="text-center">
            <div class="w-16 h-16 bg-qup-blue rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-qup-dark mb-3">Enterprise Security</h3>
            <p class="text-gray-600">
              Built with security-first principles, our products ensure your data and systems are protected with industry-leading security measures.
            </p>
          </div>
          
          <div class="text-center">
            <div class="w-16 h-16 bg-qup-blue rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-qup-dark mb-3">High Performance</h3>
            <p class="text-gray-600">
              Optimized for speed and efficiency, our products deliver exceptional performance even under high load and complex workloads.
            </p>
          </div>
          
          <div class="text-center">
            <div class="w-16 h-16 bg-qup-blue rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-qup-dark mb-3">User-Friendly</h3>
            <p class="text-gray-600">
              Intuitive interfaces and seamless user experiences make our products easy to adopt and integrate into your daily operations.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="section-padding bg-qup-dark text-white">
      <div class="container-max text-center">
        <h2 class="text-3xl md:text-4xl font-bold mb-6">
          Interested in Our Products?
        </h2>
        <p class="text-xl mb-8 text-gray-300 max-w-2xl mx-auto">
          Be among the first to experience our innovative technology solutions. Contact us to learn more about our upcoming product launches and early access opportunities.
        </p>
        <router-link to="/contact" class="btn-secondary">
          Get Early Access
        </router-link>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
const qupSecureFeatures = [
  'Real-time threat monitoring',
  'Automated incident response',
  'Compliance reporting',
  'Advanced analytics dashboard'
]

const qupFlowFeatures = [
  'Process mapping & analysis',
  'AI-powered optimization',
  'Performance metrics',
  'Automated workflow execution'
]

const qupConnectFeatures = [
  'Multi-system integration',
  'Real-time data synchronization',
  'API management',
  'Scalable architecture'
]
</script>
